import { Component, OnInit } from '@angular/core';
import { ConfirmationService } from 'primeng/api';
import { ScrBaseComponent } from 'src/app/components/base.component';
import { TableColumn } from 'src/app/models/table-columns';
import { EditUserRequest, User } from 'src/app/models/user.model';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent extends ScrBaseComponent implements OnInit {
  addUserDisplay = false;
  updateUserDisplay = false;
  mode!: string;
  users: User[] = [];
  userToEditObj!: User;
  userProfile: User = {} as User;
  tableColumn: TableColumn[] = [
    { field: 'userName', sort: true },
    { field: 'name', sort: true },
    { field: 'phone' },
    { field: 'email', sort: true },
    { field: 'role' },
    { field: 'edituser' },
    { field: 'toggleStatus' },
    { field: 'send' }
  ];

  constructor(private readonly userService: UserService,
    private confirmService: ConfirmationService) {
    super();
  }

  ngOnInit(): void {
    this.localizeTableColumns(this.tableColumn);
    this.observe(this.userService.getUserProfile(), res => {
      this.userProfile = res.user;
      return false; // no message.
    });
    this.retrieveUsers();
  }

  retrieveUsers(successMessage?: string, successMessageCode?: string) {
    this.clearMessages();
    this.observe(this.userService.getUsers(), res => {
      this.users = res.users.map(item => ({
        ...item,
        name: `${item.firstName} ${item.lastName}`
      }));
      if (successMessageCode) {
        this.displayLocalizedSuccess(successMessageCode);
      }
      else if (successMessage) {
        this.displaySuccess(successMessage, true);
      }
      return false; // no message from parent.
    });
  }

  displayAddUser() {
    this.clearMessages();
    this.mode = 'add';
    this.addUserDisplay = true;

  }

  showMessage() {
    return this.users.some(v => v.disabled);
  }

  userToEdit(user: User) {
    this.clearMessages();
    this.userToEditObj = user;
    this.mode = 'update';
    this.addUserDisplay = true;
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }

  editStatusUser(user: User) {
    if (this.userProfile.userName === user.userName) {
      return;
    }
    if (!user.disabled && this.userProfile.userName !== user.userName) {
      this.confirmService.confirm({
        key: 'confirmDisable',
        header: `Disable User ${user.userName}`,
        message: `Are you sure you want to disable "${user.userName}"? <br><br>
            Note: Disabling a user does not remove that user from the registry; rather,
            the user remains in a disabled state and cannot access any registry information or perform any actions.
            In addition, a disabled user will not receive any registry-generated email messages.`,
        accept: () => {
          const userToDisable = {
            userName: user.userName,
            disabled: true
          } as EditUserRequest;
          this.log.debug(userToDisable);
          this.observe(this.userService.editStatusUser(userToDisable), (rsp => {
            const msg = this.getFirstMessage(rsp);
            this.retrieveUsers(msg);
            return false;
          }));
        },
        reject: () => {
          this.confirmService.close();
        }
      });
    }
    if (user.disabled) {
      const userToDisable = {
        userName: user.userName,
        disabled: false
      } as EditUserRequest;
      this.observe(this.userService.editStatusUser(userToDisable), (rsp => {
        const msg = this.getFirstMessage(rsp);
        this.retrieveUsers(msg);
        return false;
      }));
    }
  }

  protected override hideChildren(): void {
    this.addUserDisplay = false;
    this.updateUserDisplay = false;
  }

  protected override reloadList(message?: string, messageCode?: string): void {
    this.retrieveUsers(message, messageCode);
  }

  resendEmail({ userId }: { userId: string | number }) {
    this.observe(this.userService.resendEmail(`${userId}`), () => {
      // nothing to do.
    });
  }

  deletePendingUser(user: User): void {
    this.clearMessages();
    this.observe(this.userService.deletePendingUser(user.userId), {
      onSuccess: (rsp) => {
        const msg = this.getFirstMessage(rsp);
        this.retrieveUsers(msg);
        return false;
      },
      onFailure: () => {
        this.retrieveUsers();
      }
    });
  }
}
